import React, { useEffect, useMemo, useState } from "react";
import { saveAs } from "file-saver";
import { Redirect } from "react-router-dom";
import { Tooltip } from "antd";
import {
  CalendarOutlined,
  CheckCircleFilled,
  DownloadOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import Header from "../../CoreComponents/Header";
import MainHeader from "../../CoreComponents/MainHeader";
import NavBar from "../../CoreComponents/NavBar";
import "./settlements.css";
import Stat from "./Stat";
import IncentiveSlabs from "./IncentiveSlabs";
import { canSettle } from "./access";
import { showMessage } from "../../Functionalities/Functions";
import confirmBox from "../../CoreComponents/confirmBox";
import { fmtDateWithWeekday, rupeesWhole } from "../Insights/format";
import { beforeTenInIndia, dateInIndia } from "./dates";
import {
  clearRiderCollections,
  entryFor,
  fetchRiderSettlement,
  loadSlabs,
  riderCsv,
  saveSlabs,
  settlementOf,
  statementLine,
  submitRiderSalary,
  withSubmittedStatement,
} from "./riderLogic";

const money = (value) => rupeesWhole(value);

const riderCount = (count) => `${count} rider${count === 1 ? "" : "s"}`;

const Money = ({ value }) => <span className={value ? undefined : "stl-zero"}>{money(value)}</span>;

const toAdminTone = (value) => (value > 0 ? "stl-owed" : value < 0 ? "stl-paid" : "stl-zero");

const NumberCell = ({ value, onChange, label }) => (
  <input
    className="stl-input stl-input--sm stl-input--num stl-cell-input"
    type="number"
    min="0"
    step="1"
    placeholder="0"
    aria-label={label}
    value={value}
    onChange={(e) => onChange(e.target.value)}
  />
);

const Empty = ({ title, text }) => (
  <div className="stl-empty">
    <span className="stl-empty__icon">
      <CalendarOutlined />
    </span>
    <span className="stl-empty__title">{title}</span>
    <span className="stl-empty__text">{text}</span>
  </div>
);

export default function RiderSettlement() {
  const [date, setDate] = useState(() => dateInIndia(new Date(), -1));
  const [sheet, setSheet] = useState(null);
  const [entries, setEntries] = useState({});
  const [slabs, setSlabs] = useState(() => loadSlabs(window.localStorage));
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState("");
  const [submitting, setSubmitting] = useState(null);
  const rows = sheet ? sheet.riders : [];

  useEffect(() => saveSlabs(window.localStorage, slabs), [slabs]);

  const entryOf = (rider) => entries[rider.id] || entryFor(rider, slabs);
  const change = (rider, field, value) => setEntries((prev) => ({ ...prev, [rider.id]: { ...entryOf(rider), [field]: value } }));

  const visible = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return rows;
    return rows.filter((item) => String(item.id).includes(query) || String(item.name || "").toLowerCase().includes(query));
  }, [rows, search]);

  if (!canSettle()) return <Redirect to="/orders" />;

  const load = async () => {
    setBusy("load");
    const body = await fetchRiderSettlement(date);
    setBusy("");
    if (body && body.Status === true) {
      setSheet({ ...body, date });
      setEntries({});
      showMessage(`${body.totals.orders} completed orders by ${body.totals.riders} riders on ${date}`, "info", "Loaded", "top", "top-right");
    } else {
      setSheet(null);
      showMessage((body && body.Error) || "Could not load the settlement", "danger", "Error!", "top", "top-right");
    }
  };

  const submit = (rider) => {
    const entry = entryOf(rider);
    const s = settlementOf(rider, entry);
    confirmBox({
      title: `Submit ${rider.name || `Rider #${rider.id}`}'s salary?`,
      message: `${money(s.salary)} for ${fmtDateWithWeekday(sheet.date)}. The rider is told at once.`,
      yesLabel: "Submit salary",
      noLabel: "Cancel",
      onYes: () => submitConfirmed(rider, entry),
    });
  };

  const submitConfirmed = async (rider, entry) => {
    setSubmitting(rider.id);
    const body = await submitRiderSalary(sheet.date, rider.id, entry);
    setSubmitting(null);
    if (body && body.Status === true) {
      setSheet((prev) => prev && withSubmittedStatement(prev, rider.id, body.statement));
      showMessage(`${rider.name}: salary ${money(body.statement.salary)} submitted`, "success", "Submitted", "top", "top-right");
    } else {
      showMessage((body && body.Error) || "Could not submit the salary", "danger", "Error!", "top", "top-right");
    }
  };

  const clear = () => {
    if (!beforeTenInIndia()) {
      showMessage("Collections are cleared before 10 AM only; after that today's orders would be mixed in.", "warning", "Too late today", "top", "top-right");
      return;
    }
    const unsubmitted = rows.filter((r) => !r.statement).length;
    const warning = unsubmitted ? ` ${unsubmitted} riders have no salary submitted for ${sheet.date} yet.` : "";
    confirmBox({
      title: "Clear delivery collection?",
      message: `Books the cash received from and the delivery charges paid to all ${rows.length} riders listed, which zeroes their balances.${warning}`,
      yesLabel: "Clear collection",
      noLabel: "Cancel",
      onYes: clearConfirmed,
    });
  };

  const clearConfirmed = async () => {
    setBusy("clear");
    const body = await clearRiderCollections(dateInIndia(), rows.map((rider) => rider.id));
    setBusy("");
    if (body && body.Status === true) {
      showMessage(`${body.booked} entries booked, ${body.skipped} zero balances skipped`, "success", "Collection cleared", "top", "top-right");
      load();
    } else {
      showMessage((body && body.Error) || "Could not clear the collection", "danger", "Error!", "top", "top-right");
    }
  };

  const download = () => {
    saveAs(new Blob([riderCsv(rows, entries, slabs)], { type: "text/csv;charset=utf-8" }), `rider-settlement-${sheet.date}.csv`);
  };

  const submittedCount = rows.filter((r) => r.statement).length;
  const hasRows = rows.length > 0;

  return (
    <div id="main-wrapper">
      <MainHeader />
      <Header name={"Rider settlement"} />
      <NavBar />
      <div className="content-body" style={{ minHeight: "100vh" }}>
        <div className="container-fluid stl">
          <section className="stl-card stl-toolbar">
            <div className="stl-toolbar__group">
              <label className="stl-field">
                <span className="stl-field__label">Day</span>
                <input className="stl-input" type="date" value={date} max={dateInIndia()} onChange={(e) => setDate(e.target.value)} />
              </label>
              <button type="button" className="stl-btn stl-btn--primary" disabled={busy !== ""} onClick={load}>
                {busy === "load" ? "Loading…" : "Load the day"}
              </button>
            </div>
          </section>

          {hasRows && (
            <section className="stl-stats">
              <Stat label="Orders" value={sheet.totals.orders} hint={`by ${riderCount(rows.length)}`} />
              <Stat label="Salaries submitted" value={`${submittedCount} / ${rows.length}`}>
                <span className="stl-progress">
                  <span style={{ width: `${(submittedCount / rows.length) * 100}%` }} />
                </span>
              </Stat>
              <Stat label="Cash in hand" value={money(sheet.totals.collection)} hint="held by riders now" />
              <Stat label="Charges owed" value={money(sheet.totals.earnings)} hint="owed to riders now" />
            </section>
          )}

          <IncentiveSlabs slabs={slabs} onChange={setSlabs} />

          <section className="stl-card stl-table-card">
            <div className="stl-table-bar">
              <div className="stl-table-bar__title">
                <h2 className="stl-title">Riders</h2>
                {sheet && (
                  <span className="stl-subtitle">
                    {fmtDateWithWeekday(sheet.date)}
                    {hasRows && (search.trim() ? ` · ${visible.length} of ${riderCount(rows.length)}` : ` · ${riderCount(rows.length)}`)}
                  </span>
                )}
              </div>
              {hasRows && (
                <div className="stl-table-bar__tools">
                  <div className="stl-search">
                    <SearchOutlined />
                    <input className="stl-input" placeholder="Search rider or ID" aria-label="Search rider or ID" value={search} onChange={(e) => setSearch(e.target.value)} />
                  </div>
                  <button type="button" className="stl-btn stl-btn--secondary" onClick={download}>
                    <DownloadOutlined /> Download CSV
                  </button>
                </div>
              )}
            </div>

            {!sheet ? (
              <Empty title="Pick a day and load it" text="Every rider with completed orders that day shows here, ready to settle." />
            ) : !hasRows ? (
              <Empty title={`No completed orders on ${fmtDateWithWeekday(sheet.date)}`} text="Pick another day and load it." />
            ) : (
              <div className="stl-scroll">
                <table className="stl-table">
                  <thead>
                    <tr>
                      <th rowSpan={2} className="stl-sticky-start">
                        Rider
                      </th>
                      <th rowSpan={2}>Orders</th>
                      <th colSpan={3} className="stl-group stl-divide">
                        From the day's orders
                      </th>
                      <th colSpan={4} className="stl-group stl-divide">
                        Adjustments
                      </th>
                      <th colSpan={2} className="stl-group stl-divide">
                        Day's cash
                      </th>
                      <th colSpan={2} className="stl-group stl-divide">
                        Balance now
                      </th>
                      <th rowSpan={2} className="stl-sticky-end stl-left">
                        Salary
                      </th>
                    </tr>
                    <tr>
                      <th className="stl-divide">Delivery charges</th>
                      <th>Tips</th>
                      <th>Surge</th>
                      <th className="stl-divide">Incentive</th>
                      <th>Special</th>
                      <th>Correction</th>
                      <th className="stl-left">Note</th>
                      <th className="stl-divide">Collected</th>
                      <th>
                        <Tooltip title="Cash collected − salary. Red: the rider hands this to the office. Green: the office pays the rider.">
                          <span className="stl-help">To admin</span>
                        </Tooltip>
                      </th>
                      <th className="stl-divide">Cash in hand</th>
                      <th>Charges owed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visible.length === 0 ? (
                      <tr>
                        <td colSpan={14} className="stl-no-match">
                          No rider matches “{search.trim()}”.
                        </td>
                      </tr>
                    ) : (
                      visible.map((rider) => {
                        const entry = entryOf(rider);
                        const s = settlementOf(rider, entry);
                        const submitted = Boolean(rider.statement);
                        const name = rider.name || `Rider #${rider.id}`;
                        return (
                          <tr key={rider.id} className={submitted ? "stl-row--done" : undefined}>
                            <td className="stl-sticky-start">
                              <div className="stl-rider">
                                <span className="stl-rider__name">{rider.name || "—"}</span>
                                <span className="stl-rider__id">#{rider.id}</span>
                              </div>
                              {submitted && (
                                <Tooltip title={statementLine(rider.statement)}>
                                  <span className="stl-pill stl-pill--done">
                                    <CheckCircleFilled /> Submitted
                                  </span>
                                </Tooltip>
                              )}
                            </td>
                            <td>
                              <div className="stl-stack">
                                <span className="stl-strong">{rider.orders || 0}</span>
                                {s.ongoing > 0 && <span className="stl-pill stl-pill--warn">{s.ongoing} ongoing</span>}
                              </div>
                            </td>
                            <td className="stl-divide">
                              <Money value={s.deliveryCharges} />
                            </td>
                            <td>
                              <Money value={s.tips} />
                            </td>
                            <td>
                              <Money value={s.surge} />
                            </td>
                            <td className="stl-divide">
                              <NumberCell value={entry.incentive} label={`${name} incentive`} onChange={(v) => change(rider, "incentive", v)} />
                            </td>
                            <td>
                              <NumberCell value={entry.special} label={`${name} special incentive`} onChange={(v) => change(rider, "special", v)} />
                            </td>
                            <td>
                              <NumberCell value={entry.correction} label={`${name} correction`} onChange={(v) => change(rider, "correction", v)} />
                            </td>
                            <td className="stl-left">
                              <input
                                className="stl-input stl-input--sm stl-note-input"
                                placeholder="Note for the rider"
                                aria-label={`${name} note`}
                                value={entry.notes}
                                onChange={(e) => change(rider, "notes", e.target.value)}
                              />
                            </td>
                            <td className="stl-divide">
                              <Money value={s.cash} />
                            </td>
                            <td className={toAdminTone(s.toAdmin)}>{money(s.toAdmin)}</td>
                            <td className="stl-divide">
                              <Money value={s.cashInHand} />
                            </td>
                            <td>
                              <Money value={s.chargesOwed} />
                            </td>
                            <td className="stl-sticky-end">
                              <div className="stl-actions">
                                <span className="stl-salary">{money(s.salary)}</span>
                                <button
                                  type="button"
                                  className={`stl-btn stl-btn--sm stl-submit ${submitted ? "stl-btn--secondary" : "stl-btn--primary"}`}
                                  disabled={submitting === rider.id}
                                  onClick={() => submit(rider)}
                                >
                                  {submitting === rider.id ? "Saving…" : submitted ? "Resubmit" : "Submit salary"}
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            )}

            <p className="stl-footnote">
              Salary = delivery charges + surge + tips + incentive + special − correction. Submit sends it to the rider's app and books the surge and
              incentive into their wallet. Cash in hand and charges owed are running balances; Clear delivery collection books them, before 10 AM only.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
